// The fullscreen view for one chart tile, every viz_type. It renders the exact
// `ChartResult` the card already holds (`ChartCard` mounts this only for a ready
// tile with rows), so opening it NEVER refetches. A refetch would need its own
// request-supersession bookkeeping (frontend/bi/CLAUDE.md).
//
// Built on @bi/shared's <Dialog> (Base UI): focus trap and restore, Escape,
// backdrop click, scroll lock and aria wiring come from it. Root, Trigger and
// Content stay in one component because Dialog.Content portals to <body> anyway.
import { Dialog } from "@bi/shared";
import type { ChartResult, ChartSpec } from "@bi/shared/types";

import { resolveGrain } from "../lib/bucket-format";
import { bucketsFromResult } from "../lib/chart-result-adapter";
import type { PivotResult } from "../lib/pivot";
import { ChartCardReady, computedAtLabel } from "./chart-card";
import { ChartDownloadButton } from "./chart-download";
import { CloseIcon, ExpandIcon } from "./icons";

export interface ChartModalProps {
  chart: ChartSpec;
  result: ChartResult;
  colors: Map<string, string>;
  /** Passed straight to `ChartCardReady` so the fullscreen view keeps the
   *  card's withhold decision for a truncated pivot. Threaded, not recomputed:
   *  two reshapes of one result can drift. */
  pivot: PivotResult | null;
  /** Three of the standing notes `ChartCard` renders on the card (design doc
   *  §4/§7). A fullscreen view that drops them is misleading, not just less
   *  detailed. The card's grain, single-bucket, time-of-day and pivot notes
   *  are not carried here. */
  inactiveFilterCount: number;
  constraintList: string;
  ignoresDateRange: boolean;
}

export function ChartModal({
  chart,
  result,
  colors,
  pivot,
  inactiveFilterCount,
  constraintList,
  ignoresDateRange,
}: ChartModalProps) {
  return (
    <Dialog.Root>
      <Dialog.Trigger
        render={
          <button
            type="button"
            className="bi-card__action"
            aria-label={`View ${chart.slice_name} fullscreen`}
            title="View fullscreen"
          />
        }
      >
        <ExpandIcon size={16} />
      </Dialog.Trigger>
      <Dialog.Content className="bi-chart-modal">
        <header className="bi-chart-modal__head">
          <Dialog.Title className="bi-chart-modal__title">{chart.slice_name}</Dialog.Title>
          {/* The download is offered here too, not only on the card: a reader
           *  who opened a tile fullscreen to study it is the likeliest one to
           *  want the numbers behind it, and the card's own copy of the button
           *  is under the backdrop while this is open. The same component, so
           *  the two cannot disagree about what a chart's CSV contains. */}
          <div className="bi-chart-modal__actions">
            <ChartDownloadButton
              chart={chart}
              result={result}
              className="bi-chart-modal__icon-button"
              iconSize={20}
            />
            <Dialog.Close
              render={
                <button type="button" className="bi-chart-modal__icon-button" aria-label="Close">
                  <CloseIcon size={20} />
                </button>
              }
            />
          </div>
        </header>
        {chart.description ? (
          <Dialog.Description className="bi-chart-modal__description">
            {chart.description}
          </Dialog.Description>
        ) : null}
        {inactiveFilterCount > 0 ? (
          <p className="bi-card__note">
            {inactiveFilterCount} filter{inactiveFilterCount === 1 ? "" : "s"} do
            {inactiveFilterCount === 1 ? "es" : ""} not apply to this chart
          </p>
        ) : null}
        {constraintList ? (
          <p className="bi-card__note">This chart is always limited to {constraintList}</p>
        ) : null}
        {ignoresDateRange ? (
          <p className="bi-card__note">
            This chart has no date column, so it covers all time regardless of the date range
          </p>
        ) : null}
        <div className="bi-chart-modal__body">
          <ChartCardReady
            chart={chart}
            result={result}
            colors={colors}
            pivot={pivot}
            grain={resolveGrain(result.applied_time_grain, bucketsFromResult(chart, result))}
            expandedPresentation
          />
        </div>
        <p className="bi-card__stamp">{computedAtLabel(result.computed_at)}</p>
      </Dialog.Content>
    </Dialog.Root>
  );
}
